import React, { useMemo, useRef, useState, useEffect } from 'react';

import { dashboardCustomsInstructionApis } from '@apis/next/customsInstructionApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import { ArrivalNoticeForm } from '@components/admin/booking/ArrivalNoticeForm';
import { BookingDetailsManagementInfo } from '@components/admin/booking/BookingDetailsManagementInfo';
import {
  BookingInquiryDialogs,
  BookingInquiryDialogsRef,
} from '@components/admin/booking/BookingInquiryDialogs';
import { BookingInquiryProfitPanel } from '@components/admin/booking/BookingInquiryProfitPanel';
import { BookingStatusFlows } from '@components/admin/booking/BookingStatusFlows';
import { EditPreAlertFieldDialog } from '@components/admin/booking/EditPreAlertFieldDialog';
import {
  BookingDetailsContext,
  BookingDetailsContextValues,
} from '@components/admin/booking/helpers';
import { InstructionFormEntrances as AdminInstructionFormEntrances } from '@components/admin/booking/InstructionFormEntrances';
import { InvoiceDetailsPanel } from '@components/admin/booking/InvoiceDetailsPanel';
import { InvoicePanel } from '@components/admin/booking/InvoicePanel';
import { MemoForm } from '@components/admin/booking/MemoForm';
import { PaymentSlipPanel } from '@components/admin/booking/PaymentSlipPanel';
import { PreAlertForm } from '@components/admin/booking/PreAlertForm';
import { SeaWaybillForm2 } from '@components/admin/booking/SeaWaybillForm2';
import { TrackingDataDetails } from '@components/admin/booking/TrackingDataDetails';
import { FormTitle } from '@components/admin/Forms';
import { Layout } from '@components/admin/Layout';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { Tab } from '@components/Tab';
import { BookingInquiryDisplay } from '@components/utilities/BookingInquiry/BookingInquiryDisplay';
import { bookingInquiryStrings, bookingInquiryTradeConditionStrings } from '@data/bookingInquiry';
import { bookingInquiryProfitStrings } from '@data/bookingInquiryProfit';
import { paymentSlipStrings } from '@data/paymentSlip';
import { usePatchBookingInquiryMemo } from '@hooks/mutation/dashboard/usePatchBookingInquiryMemo';
import { usePatchPreAlert } from '@hooks/mutation/dashboard/usePatchPreAlert';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useDeliveryInstruction } from '@hooks/queries/dashboard/useDeliveryInstruction';
import { useUser } from '@hooks/queries/useUser';
import { useSearch } from '@hooks/useSearch';
import Collapse from '@material-ui/core/Collapse';
import { AdminInquiryDetailHelper } from '@models/BookingInquiry';
import type { PreAlertDetails } from '@models/next/PreAlert';
import { bookingDetailsSearchType, PatchPreAlert } from '@models/PreAlert';
import { UserHelper } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { getConfig } from '@utils/config';
import { FormError } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getBreadcrumb, url } from '@utils/pages';
import { mapOptional, toJST } from '@utils/types';
import { useLocalStorageState } from '@utils/useLocalStorageState';
import { useShallowMemo } from '@utils/useShallowMemo';
import clsx from 'clsx';
import { z } from 'zod';

const strings = createStrings({
  ja: {
    title: '詳細',
    managementIntelligence: '管理情報',
    customerIntelligence: '顧客情報',
    bookingInquiry: '予約内容',
    companyName: '会社名',
    memberNo: '会員番号',
    author: '作成者',
    d2dNo: 'D2D番号',
    createdAd: '受付時間',
    status: '対応状況',
    status2: 'ステータス',
    cancel: 'キャンセル',
    memo: 'メモ',
    memoUpdated: 'メモを更新しました',
    invoice: '請求書',
    invoiceDetail: '請求明細',
    bookingData: '予約データ',
    createNewPreAlert: 'PRE ALERTを新規作成',
    dateTime: '日時',
    remark: '備考',
    document: '書類',
    editDisabledAtdExists: 'ATDが確認された上は、予約内容は変更不可です。',
    editDisabledNotSalesperson: '担当者のみが変更可能です。',
    editDisabledPreAlertLocked: 'PreAlertがロックされたので、予約内容の変更ができません。',
    changeLog: '変更履歴',
    invoiceAddButton: '請求名目を追加する',
    creatAN: 'A/Nを作成する',
    createNewSeaWaybill: 'SEA WAYBILLを新規作成',
    unableToCreateSeaWaybill:
      '船舶が出港するまで、もしくは Pre Alert がロックされるまで Sea Waybill の作成ができません。',
    unableToLoadDeliveryGuidance:
      'ETAデータが空の場合、この機能は使用できません。先にPreAlertを作成してください。',
    toKikanSystem: '基幹システムへ',
    toZendeskTicket: 'Zendesk Ticketへ',
    ledgerNumber: '台帳番号：',
    customsDeliveryInstruction: '通関書類・納品指示',
    paymentSlip: paymentSlipStrings.payment_slip,
    manangementTool: '案件管理ツール',
    needJobCodeError:
      'PreAlertをロックして台帳番号を取得する上、リンクで基幹システムに移動できます。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    flex: 1,
    padding: '1.5rem 2rem',
    ...common.verticalSpace3,
  },
  textDisplayYContainer: {
    ...common.flexColumn,
    ...common.middleText,
    alignItems: 'flex-start',
    padding: 5,
    color: theme.palette.grey[700],
  },
  textDisplayBottomItem: {
    ...common.flexInline,
    width: 240,
    '& a': {
      fontWeight: 'bold',
      alignItems: 'center',
      textDecorationLine: 'underline',
      color: theme.palette.primary.main,
    },
    padding: 20,
  },
  textDisplayYItem: {
    fontWeight: 500,
  },
  innerTabPanel: {
    padding: '1rem',
  },
  table: {
    '& td': {
      textAlign: 'center',
    },
  },
  memoForm: {
    ...common.flexColumn,
  },
  input: {
    ...common.smallerText,
  },
  textInput: {
    padding: 5,
  },
  updateButton: {
    alignSelf: 'left',
    width: 60,
    margin: '0.5rem 0',
  },
  updatedInfo: {
    ...common.smallerText,
    color: theme.palette.primary.main,
    fontWeight: 500,
    marginTop: '0.1rem',
  },
  invoiceOpBox: {
    ...common.flexInline,
    ...common.horizontalSpace1,
  },
  anBox: {
    marginTop: 21,
    marginLeft: 14,
  },
  bottomContainer: {
    ...common.flexColumn,
    ...common.itemsEnd,
    ...common.middleText,
    ...common.textGray500,
    rowGap: '0.5rem',
  },
  tabTitle: {
    position: 'relative',
    maxWidth: 'fit-content',
    margin: 'auto',
  },
  badge: {
    height: 14,
    width: 14,
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.dark,
    position: 'absolute',
    top: -5,
    right: -5,
    paddingBottom: 2,
    zIndex: 2,
  },
  bookingInquiryDisplayContainerWrapper: {
    marginTop: 16,
    padding: 8,
    backgroundColor: theme.palette.grey[50],
  },
}));

export const BookingDetails: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(bookingDetailsSearchType);

  const [showMemoUpdated, setShowMemoUpdated] = useState<boolean>(false);

  const bookingInquiryDialogsRef = useRef<BookingInquiryDialogsRef>(null);

  const statusFlowsRef = useRef<HTMLDivElement>(null);

  const preAlert = dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no: search.d2dNo,
    },
  });
  const bookingInquiry = useBookingInquiryDetailByD2dNo(search.d2dNo);

  const deliveryInstruction = useDeliveryInstruction(search.d2dNo, {
    enabled: !!preAlert.data?.eta,
  });
  const customsInstructionQuery = dashboardCustomsInstructionApis.useGet(
    '/dashboard/customs-instruction/:d2d_no',
    {
      params: {
        d2d_no: search.d2dNo,
      },
    },
    {
      enabled: !!preAlert.data?.eta,
    },
  );

  const patchPreAlert = usePatchPreAlert(search.d2dNo);
  const patchInquiryMemo = usePatchBookingInquiryMemo(search.d2dNo);

  const user = useUser();

  const isSalesperson =
    mapOptional(
      bookingInquiry.data,
      (u) => user.data && new AdminInquiryDetailHelper(u).isSalesperson(user.data),
    ) ?? false;

  const canEdit =
    mapOptional(
      bookingInquiry.data,
      (u) =>
        user.data &&
        preAlert.data &&
        new AdminInquiryDetailHelper(u).canEdit(user.data, preAlert.data),
    ) ?? false;

  const isJapanStaff = user.data ? new UserHelper(user.data).isJapanStaff : false;
  const isBeforeDeparture =
    mapOptional(preAlert.data, (p) => AdminInquiryDetailHelper.isBeforeDeparture(p)) ?? true;

  const breadcrumb = useMemo(
    () => [
      ...{
        current: isBeforeDeparture
          ? getBreadcrumb('/admin/booking/CurrentBookingsBeforeDeparture')
          : getBreadcrumb('/admin/booking/CurrentBookingsAfterDeparture'),
        past_finished: getBreadcrumb('/admin/booking/PastBookings'),
        past_canceled: getBreadcrumb('/admin/booking/CanceledBookings'),
        all: getBreadcrumb('/admin/booking/AllBookings'),
      }[search.type],
      String(search.d2dNo),
    ],
    [search.d2dNo, search.type, isBeforeDeparture],
  );

  const isPreAlertEmpty = preAlert.data?.is_empty ?? true;

  const useExpanded = (name: string, defaultExpanded = true) => {
    return useLocalStorageState(`BOOKING_DETAILS_${name}`, z.boolean(), defaultExpanded);
  };

  const [managementInfoExpanded, setManagementInfoExpanded] = useExpanded(
    'MANAGEMENT_INFO_EXPANDED',
  );

  const [memoFormExpanded, setMemoFormExpanded] = useExpanded('MEMO_FORM_EXPANDED');

  const [bookingStatusFlowsExpanded, setBookingStatusFlowsExpanded] = useExpanded(
    'BOOKING_STATUS_FlOWS_EXPANDED',
  );

  const [trackingDataDetailsExpanded, setTrackingDataDetailsExpanded] = useExpanded(
    'RACKING_DATA_DETAILS_EXPANDED',
    false,
  );

  const [invoiceExpanded, setInvoiceExpanded] = useExpanded('INVOICE_EXPANDED', false);

  const [invoiceDetailsExpanded, setInvoiceDetailsExpanded] = useExpanded(
    'INVOICE_DETAILS_EXPANDED',
    false,
  );

  const [bookingDataExpanded, setBookingDataExpanded] = useExpanded('BOOKING_DATA_EXPANDED', true);

  const [paymentSlipExpanded, setPaymentSlipExpanded] = useExpanded('PAYMENT_SLIP_EXPANDED', true);

  const [profitPanelExpanded, setProfitPanelExpanded] = useExpanded('PROFIT_PANEL_EXPANDED', true);

  const [UIState, setUIState] = useState(() => ({
    changeAssigneeDialog: {
      open: false,
    },
    cancelBookingDialog: {
      open: false,
    },
    addTransportStatusDialog: {
      open: false,
    },
    preAlertForm: {
      open: !isPreAlertEmpty,
    },

    editPreAlertFormDialog: {
      open: false,
      /**
       * a target field name
       */
      name: undefined,
      /**
       * a target field label
       */
      fieldLabel: undefined,
      /**
       * current value of a target field
       */
      before: undefined,
    } as {
      open: boolean;
      name?: keyof PreAlertDetails;
      fieldLabel?: string;
      before?: string;
    },
  }));

  useEffect(() => {
    setUIState((state) => {
      if (preAlert.data == null || state.preAlertForm.open === true) {
        return state;
      }

      return {
        ...state,
        preAlertForm: { open: !(preAlert.data?.is_empty ?? true) },
      };
    });
  }, [UIState.preAlertForm.open, setUIState, preAlert.data?.is_empty]);

  const loading = preAlert.isLoading || bookingInquiry.isLoading || user.isLoading;
  const errors = [preAlert.error, bookingInquiry.error];

  const renderBottom = () => {
    const href = preAlert.data?.job_code
      ? `https://www.fcstandard.work/scripts/FCSL/Programs/Customs/MainFrame.asp?JobCode=${preAlert.data?.job_code}`
      : undefined;

    return (
      <div className={classes.bottomContainer}>
        <a
          href={href}
          target="_blank"
          title={!href ? strings.needJobCodeError : undefined}
          className={clsx(href && classes.link, classes.underline)}
        >
          {strings.toKikanSystem}
        </a>

        <span className={clsx(classes.middleText, preAlert.data?.job_code && classes.textBlack)}>
          {strings.ledgerNumber}
          {preAlert.data?.job_code ? preAlert.data?.job_code : formStrings.none}
        </span>
      </div>
    );
  };

  const bookingDetailsContextValues: BookingDetailsContextValues = useShallowMemo(() => ({
    preAlert: preAlert.data!,
    bookingInquiry: bookingInquiry.data!,
    user: user.data!,
  }));

  return (
    <Layout
      title={`${strings.title} - ${search.d2dNo}`}
      loading={loading}
      errors={errors}
      breadcrumb={breadcrumb}
    >
      {() => (
        <BookingDetailsContext.Provider value={bookingDetailsContextValues}>
          <EditPreAlertFieldDialog
            name={UIState.editPreAlertFormDialog.name}
            open={UIState.editPreAlertFormDialog.open}
            before={UIState.editPreAlertFormDialog.before}
            fieldLabel={UIState.editPreAlertFormDialog.fieldLabel}
            onClose={() => {
              setUIState((state) => {
                return { ...state, editPreAlertFormDialog: { open: false } };
              });
            }}
            onSubmit={async (values) => {
              const detail = values.reason ?? '';
              await patchPreAlert.mutateAsync({
                detail,
                [UIState.editPreAlertFormDialog.name as string]: values.after,
              } as PatchPreAlert & { [key: string]: string | number });
            }}
          />
          <div className={classes.container}>
            <div>
              <FormTitle
                title={
                  <>
                    {strings.managementIntelligence}
                    {getConfig().zendeskEnabled && (
                      <a
                        className={classes.link}
                        style={{ marginLeft: '1rem' }}
                        target="_blank"
                        href={`${getConfig().zendeskDashboard}/agent/dashboard`}
                      >
                        {strings.toZendeskTicket}
                      </a>
                    )}
                  </>
                }
                noEdit
                noExpand={false}
                expanded={managementInfoExpanded}
                onChangeExpanded={setManagementInfoExpanded}
              />
              <Collapse in={managementInfoExpanded}>
                <BookingDetailsManagementInfo
                  onScrollToManagementTool={() =>
                    statusFlowsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                />
              </Collapse>
            </div>
            <div>
              <FormTitle
                title={
                  <>
                    {bookingInquiryStrings.memo}
                    {showMemoUpdated && (
                      <>
                        <div style={{ width: '1rem' }} />
                        <div className={classes.updatedInfo}>{strings.memoUpdated}</div>
                      </>
                    )}
                  </>
                }
                noEdit
                noExpand={false}
                expanded={memoFormExpanded}
                onChangeExpanded={setMemoFormExpanded}
              />
              {bookingInquiry.data && (
                <Collapse in={memoFormExpanded}>
                  <MemoForm
                    memo={bookingInquiry.data?.memo ?? ''}
                    id={bookingInquiry.data?.id!}
                    isLoading={patchInquiryMemo.isLoading}
                    onSubmit={async (values) => {
                      await patchInquiryMemo.mutateAsync(values);
                      setShowMemoUpdated(true);
                      setTimeout(() => setShowMemoUpdated(false), 5000);
                    }}
                  />
                </Collapse>
              )}
            </div>
            {isJapanStaff && (
              <div ref={statusFlowsRef}>
                <FormTitle
                  title={strings.manangementTool}
                  noEdit
                  noExpand={false}
                  expanded={bookingStatusFlowsExpanded}
                  onChangeExpanded={setBookingStatusFlowsExpanded}
                />
                {bookingInquiry.data && (
                  <Collapse in={bookingStatusFlowsExpanded}>
                    <BookingStatusFlows d2dNo={search.d2dNo} />
                  </Collapse>
                )}
              </div>
            )}
            <div>
              <FormTitle
                title={strings.bookingData}
                noEdit
                noExpand={false}
                expanded={bookingDataExpanded}
                onChangeExpanded={setBookingDataExpanded}
              />
              <Collapse in={bookingDataExpanded}>
                <Tab
                  activeId={search.tabActiveId ?? 'bookingInquiry'}
                  onChangeActiveId={(tabActiveId) => updateSearch({ tabActiveId })}
                >
                  <div className={classes.flexInline}>
                    <Tab.Button htmlFor="bookingInquiry">{strings.bookingInquiry}</Tab.Button>
                    <Tab.Button htmlFor="preAlert">PRE ALERT</Tab.Button>
                    <Tab.Button htmlFor="seaWaybill">SEA WAYBILL</Tab.Button>
                    {isJapanStaff && (
                      <Tab.Button htmlFor="instruction">
                        <div className={classes.tabTitle}>
                          {(deliveryInstruction.data?.cargo_changed ||
                            customsInstructionQuery.data?.use_rcep_changed) && (
                            <div className={classes.badge} />
                          )}
                          {strings.customsDeliveryInstruction}
                        </div>
                      </Tab.Button>
                    )}
                    {isJapanStaff && (
                      <Tab.Button htmlFor="arrivalNotice">ARRIVAL NOTICE</Tab.Button>
                    )}
                  </div>
                  <div>
                    <Tab.Panel id="bookingInquiry">
                      {bookingInquiry.data && preAlert.data && (
                        <>
                          <div className={classes.bookingInquiryDisplayContainerWrapper}>
                            <BookingInquiryDisplay
                              bookingInquiry={bookingInquiry.data}
                              onOpenForm={(name) =>
                                bookingInquiryDialogsRef.current?.onOpenForm(name)
                              }
                              editDisabled={
                                !isSalesperson ||
                                preAlert.data?.atd != null ||
                                preAlert.data?.is_manually_locked
                              }
                              editDisabledReason={
                                !isSalesperson
                                  ? strings.editDisabledNotSalesperson
                                  : preAlert.data?.atd != null
                                  ? strings.editDisabledAtdExists
                                  : preAlert.data?.is_manually_locked
                                  ? strings.editDisabledPreAlertLocked
                                  : undefined
                              }
                              type="admin"
                              cargosDisplayVariant="inline"
                            />
                          </div>
                          <BookingInquiryDialogs
                            ref={bookingInquiryDialogsRef}
                            adminInquiryDetail={bookingInquiry.data}
                            preAlertDetails={preAlert.data}
                          />

                          <div style={{ height: 16 }} />

                          <div
                            className={clsx(
                              classes.textGray800,
                              classes.middleText,
                              classes.flexInline,
                              classes.justifyBetween,
                            )}
                          >
                            <span>
                              Latest Update: {toJST(new Date(bookingInquiry.data.updated_at!))}
                            </span>

                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={`${url('/utilities/Bookings/BookingInquiryLogs')}?d2d_no=${
                                bookingInquiry.data.d2d_no
                              }&admin=${true}`}
                              className={clsx(classes.link, classes.baseButton)}
                            >
                              {strings.changeLog}
                            </a>
                          </div>
                        </>
                      )}
                    </Tab.Panel>
                    <Tab.Panel id="preAlert">
                      <Alert open={Boolean(patchPreAlert.error)} severity="error">
                        {patchPreAlert.error instanceof FormError
                          ? patchPreAlert.error.getDisplayMessage()
                          : String(patchPreAlert.error)}
                      </Alert>
                      <div className={classes.innerTabPanel}>
                        {UIState.preAlertForm.open ? (
                          <PreAlertForm
                            edit={!isPreAlertEmpty}
                            onExportClick={() => {
                              window
                                ?.open(
                                  `${url('/admin/booking/ExportPreAlert')}?d2dNo=${search.d2dNo}`,
                                  '_blank',
                                )
                                ?.focus();
                            }}
                            onHistoryClick={() => {
                              if (preAlert.data?.booking_inquiry !== null) {
                                const targetUrl = `${url('/admin/booking/PrintPreAlertLog')}?id=${
                                  preAlert.data?.booking_inquiry
                                }`;
                                window.open(targetUrl, '_blank')?.focus();
                              }
                            }}
                            onEdit={(name, label) => {
                              setUIState((state) => {
                                return {
                                  ...state,
                                  editPreAlertFormDialog: {
                                    open: true,
                                    name,
                                    fieldLabel: label,
                                    before: (preAlert.data![name] ?? '').toString(),
                                  },
                                };
                              });
                            }}
                          />
                        ) : (
                          <Button
                            color="primary"
                            disabled={!isSalesperson}
                            title={!isSalesperson ? strings.editDisabledNotSalesperson : undefined}
                            size="small"
                            text={strings.createNewPreAlert}
                            shape="round"
                            onClick={() => {
                              if (isSalesperson) {
                                setUIState((state) => {
                                  return {
                                    ...state,
                                    preAlertForm: { ...state.preAlertForm, open: true },
                                  };
                                });
                              }
                            }}
                          />
                        )}
                      </div>
                    </Tab.Panel>
                    <Tab.Panel id="seaWaybill">
                      <SeaWaybillForm2 />
                    </Tab.Panel>
                    <Tab.Panel id="instruction">
                      {preAlert.data?.eta ? (
                        <AdminInstructionFormEntrances d2dNo={search.d2dNo} />
                      ) : (
                        <div
                          style={{ padding: '1rem' }}
                          className={clsx(classes.middleText, classes.textGray600)}
                        >
                          {strings.unableToLoadDeliveryGuidance}
                        </div>
                      )}
                    </Tab.Panel>
                    <Tab.Panel id="arrivalNotice">
                      <ArrivalNoticeForm />
                    </Tab.Panel>
                  </div>
                </Tab>
              </Collapse>
              {renderBottom()}
            </div>
            <div>
              <FormTitle
                title={strings.status2}
                noEdit
                noExpand={false}
                expanded={trackingDataDetailsExpanded}
                onChangeExpanded={setTrackingDataDetailsExpanded}
              />
              {bookingInquiry.data?.d2d_no && preAlert.data && canEdit && (
                <Collapse in={trackingDataDetailsExpanded}>
                  <TrackingDataDetails
                    bookingId={bookingInquiry.data?.id!}
                    d2dNo={bookingInquiry.data?.d2d_no}
                    preAlert={preAlert.data}
                    isClient={false}
                    canEdit={canEdit}
                  />
                </Collapse>
              )}
            </div>
            <div>
              <FormTitle
                title={
                  <>
                    {strings.invoiceDetail}
                    {bookingInquiry.data && (
                      <span className={classes.textPrimary} style={{ marginLeft: '1rem' }}>
                        {bookingInquiryTradeConditionStrings[bookingInquiry.data.trade_condition]}（
                        {bookingInquiry.data.cargos.cargos_by_port
                          ? bookingInquiryStrings.noPickup
                          : bookingInquiryStrings.needsPickup}
                        ）
                      </span>
                    )}
                  </>
                }
                noEdit
                noExpand={false}
                expanded={invoiceDetailsExpanded}
                onChangeExpanded={setInvoiceDetailsExpanded}
              />

              <Collapse in={invoiceDetailsExpanded}>
                <InvoiceDetailsPanel />
              </Collapse>
            </div>
            {isJapanStaff && (
              <div>
                <FormTitle
                  title={strings.invoice}
                  noEdit
                  noExpand={false}
                  expanded={invoiceExpanded}
                  onChangeExpanded={setInvoiceExpanded}
                />
                <Collapse in={invoiceExpanded}>
                  <InvoicePanel />
                </Collapse>
              </div>
            )}
            {isJapanStaff && (
              <div>
                <FormTitle
                  title={strings.paymentSlip}
                  noEdit
                  noExpand={false}
                  expanded={paymentSlipExpanded}
                  onChangeExpanded={setPaymentSlipExpanded}
                />
                <Collapse in={paymentSlipExpanded}>
                  <PaymentSlipPanel />
                </Collapse>
              </div>
            )}
            {isJapanStaff && (
              <div>
                <FormTitle
                  title={bookingInquiryProfitStrings.profit}
                  noEdit
                  noExpand={false}
                  expanded={profitPanelExpanded}
                  onChangeExpanded={setProfitPanelExpanded}
                />
                <Collapse in={profitPanelExpanded}>
                  {preAlert.data && <BookingInquiryProfitPanel />}
                </Collapse>
              </div>
            )}
          </div>
        </BookingDetailsContext.Provider>
      )}
    </Layout>
  );
};
